import type { PlayerSnapshot } from '@roadworld/shared';

export interface InterpolatedPose {
  px: number;
  py: number;
  pz: number;
  qx: number;
  qy: number;
  qz: number;
  qw: number;
  steer: number;
  speed: number;
  /** True if we held the last pose (snapshot gap too large). */
  stale: boolean;
}

/**
 * Tiny ring buffer of the last two snapshots for one remote player.
 * Position is stored as already-converted world XYZ (the network layer converts
 * lat/lng → world before pushing).
 */
export class SnapshotBuffer {
  private s0: SnapshotWithWorld | null = null;
  private s1: SnapshotWithWorld | null = null;

  push(snap: PlayerSnapshot, worldX: number, worldY: number, worldZ: number): void {
    this.s0 = this.s1;
    this.s1 = { snap, worldX, worldY, worldZ };
  }

  /** Latest known pose, no interpolation. */
  latest(): SnapshotWithWorld | null {
    return this.s1;
  }

  /**
   * Sample interpolated pose at `renderTime` (server-clock ms).
   * Returns null if there's no data yet.
   */
  sample(renderTime: number): InterpolatedPose | null {
    if (!this.s1) return null;
    if (!this.s0) {
      return poseFrom(this.s1, true);
    }
    const t0 = this.s0.snap.t;
    const t1 = this.s1.snap.t;

    if (renderTime <= t0) return poseFrom(this.s0, false);
    if (renderTime <= t1) {
      const span = Math.max(1, t1 - t0);
      const a = (renderTime - t0) / span;
      return lerpPose(this.s0, this.s1, a, false);
    }

    // Extrapolate forward from s1 using its velocity, capped to 100ms.
    const ahead = renderTime - t1;
    if (ahead < 500) {
      const capped = Math.min(ahead, 100) / 1000;
      return {
        px: this.s1.worldX + this.s1.snap.vx * capped,
        py: this.s1.worldY + this.s1.snap.vy * capped,
        pz: this.s1.worldZ + this.s1.snap.vz * capped,
        qx: this.s1.snap.qx,
        qy: this.s1.snap.qy,
        qz: this.s1.snap.qz,
        qw: this.s1.snap.qw,
        steer: this.s1.snap.steer,
        speed: this.s1.snap.speed,
        stale: false,
      };
    }
    return poseFrom(this.s1, true);
  }
}

interface SnapshotWithWorld {
  snap: PlayerSnapshot;
  worldX: number;
  worldY: number;
  worldZ: number;
}

function poseFrom(s: SnapshotWithWorld, stale: boolean): InterpolatedPose {
  return {
    px: s.worldX,
    py: s.worldY,
    pz: s.worldZ,
    qx: s.snap.qx,
    qy: s.snap.qy,
    qz: s.snap.qz,
    qw: s.snap.qw,
    steer: s.snap.steer,
    speed: s.snap.speed,
    stale,
  };
}

function lerpPose(
  a: SnapshotWithWorld,
  b: SnapshotWithWorld,
  t: number,
  stale: boolean,
): InterpolatedPose {
  const px = a.worldX + (b.worldX - a.worldX) * t;
  const py = a.worldY + (b.worldY - a.worldY) * t;
  const pz = a.worldZ + (b.worldZ - a.worldZ) * t;
  // Slerp the quat. Negate b if dot < 0 to take the short path.
  let bx = b.snap.qx;
  let by = b.snap.qy;
  let bz = b.snap.qz;
  let bw = b.snap.qw;
  const ax = a.snap.qx;
  const ay = a.snap.qy;
  const az = a.snap.qz;
  const aw = a.snap.qw;
  let dot = ax * bx + ay * by + az * bz + aw * bw;
  if (dot < 0) {
    bx = -bx;
    by = -by;
    bz = -bz;
    bw = -bw;
    dot = -dot;
  }
  let s0 = 1 - t;
  let s1 = t;
  if (dot < 0.9995) {
    const theta = Math.acos(Math.max(-1, Math.min(1, dot)));
    const sinTheta = Math.sin(theta);
    if (sinTheta > 1e-6) {
      s0 = Math.sin((1 - t) * theta) / sinTheta;
      s1 = Math.sin(t * theta) / sinTheta;
    }
  }
  let qx = ax * s0 + bx * s1;
  let qy = ay * s0 + by * s1;
  let qz = az * s0 + bz * s1;
  let qw = aw * s0 + bw * s1;
  const n = Math.sqrt(qx * qx + qy * qy + qz * qz + qw * qw) || 1;
  qx /= n;
  qy /= n;
  qz /= n;
  qw /= n;

  const steer = a.snap.steer + (b.snap.steer - a.snap.steer) * t;
  const speed = a.snap.speed + (b.snap.speed - a.snap.speed) * t;
  return { px, py, pz, qx, qy, qz, qw, steer, speed, stale };
}
